import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export interface AttendanceRequestDto {
  studentId: number;
  statusId: number;
  remarks?: string;
}

export interface BulkAttendanceRequestDto {
  staffId: number;
  classId: number;
  sessionId: number;
  attendanceDate: string;
  students: AttendanceRequestDto[];
}

export interface AttendanceResponseDto {
  id: number;
  studentId: number;
  studentName: string;
  statusId: number;
  statusCode: string;
  remarks?: string;
  classId: number;
  sessionId: number;
  staffId?: number;
  teacherId?: number;
  attendanceDate: string;
  lastUpdatedTime: string;
}

export interface GetStudentAttendanceParams {
  classId: number;
  sessionId: number;
  date?: string;
  startDate?: string;
  endDate?: string;
}

export interface AttendanceStatusRequestDto {
  id?: number;
  statusCode: string;
}

export interface AttendanceStatusResponseDto {
  id: number;
  statusCode: string;
}

const API_URL = `${process.env.REACT_APP_API_URL}/student-attendance`;
const ATTENDANCE_STATUS_API_URL = `${process.env.REACT_APP_API_URL}/attendance-status`;

export const createBulkStudentAttendance = async (
  data: BulkAttendanceRequestDto
): Promise<AttendanceResponseDto[]> => {
  const response = await axios.post(
    `${API_URL}/bulk`,
    data
  );
  return response.data;
};

export const getStudentAttendance = async (
  params: GetStudentAttendanceParams
): Promise<AttendanceResponseDto[]> => {
  const response = await axios.get(API_URL, {
    params: {
      classId: params.classId,
      sessionId: params.sessionId,
      startDate: params.startDate,
      endDate: params.endDate,
    },
  });

  return response.data;
};

export const getAttendanceStatuses = async (): Promise<AttendanceStatusResponseDto[]> => {
  const response = await axios.get(ATTENDANCE_STATUS_API_URL);
  return response.data;
};

export const createAttendanceStatus = async (
  data: AttendanceStatusRequestDto
): Promise<AttendanceStatusResponseDto> => {
  const response = await axios.post(ATTENDANCE_STATUS_API_URL, data);
  return response.data;
};

export const getAttendanceByDate = async (
  classId: number,
  sessionId: number,
  date: string
): Promise<AttendanceResponseDto[]> => {
  const response = await axios.get(`${API_URL}/by-date`, {
    params: {
      classId,
      sessionId,
      date
    },
  });
  return response.data;
};

export function useAttendance(params?: GetStudentAttendanceParams) {
  const queryClient = useQueryClient();

  const bulkAttendanceMutation = useMutation({
    mutationFn: createBulkStudentAttendance,
    onSuccess: (data, variables) => {
      // Invalidate all attendance queries to ensure the UI updates
      queryClient.invalidateQueries({
        queryKey: ["student-attendance"],
      });
    },
  });

  const getAttendanceQuery = useQuery<AttendanceResponseDto[]>({
    queryKey: [
      "student-attendance",
      params?.classId,
      params?.sessionId,
      params?.startDate,
      params?.endDate,
    ],
    queryFn: () => {
      if (!params) return Promise.resolve([]);
      return getStudentAttendance(params);
    },
    enabled:
      !!params?.classId &&
      !!params?.sessionId &&
      !!params?.startDate &&
      !!params?.endDate,
  });

  // Query to check if attendance exists for a specific date
  const useAttendanceByDateQuery = (classId?: number, sessionId?: number, date?: string) => {
    return useQuery<AttendanceResponseDto[]>({
      queryKey: ["student-attendance-by-date", classId, sessionId, date],
      queryFn: () => {
        if (!classId || !sessionId || !date) return Promise.resolve([]);
        return getAttendanceByDate(classId, sessionId, date);
      },
      enabled: !!classId && !!sessionId && !!date,
    });
  };

  const attendanceStatusQuery = useQuery<
    AttendanceStatusResponseDto[]
  >({
    queryKey: ["attendance-status"],
    queryFn: getAttendanceStatuses,
    staleTime: 10 * 60 * 1000, // cache for 10 minutes
  });

  const createAttendanceStatusMutation = useMutation({
    mutationFn: createAttendanceStatus,
    onSuccess: () => {
      // refresh status list
      queryClient.invalidateQueries({
        queryKey: ["attendance-status"],
      });
    },
  });

  return {
    bulkAttendanceMutation,
    getAttendanceQuery,
    useAttendanceByDateQuery,
    attendanceStatusQuery,
    createAttendanceStatusMutation,
  };
}